import React, { useState, useMemo } from 'react';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronDown, Check, Plus, Briefcase, EyeOff } from 'lucide-react';

const STORAGE_KEY = 'workspaceHiddenIds';

function loadHiddenIds(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

export const WorkspaceSelector: React.FC = () => {
  const { workspaces, currentWorkspace, setCurrentWorkspace } = useWorkspaces();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const hiddenIds = useMemo(() => loadHiddenIds(), []);

  const visibleWorkspaces = useMemo(() => {
    const currentId = currentWorkspace?.id;
    return workspaces.filter((w) => !hiddenIds.has(w.id) || w.id === currentId);
  }, [workspaces, hiddenIds, currentWorkspace?.id]);

  const isCurrentHidden = !!(
    currentWorkspace?.id && hiddenIds.has(currentWorkspace.id)
  );

  const handleSelectWorkspace = (workspaceId: string) => {
    const workspace =
      visibleWorkspaces.find((w) => w.id === workspaceId) ??
      workspaces.find((w) => w.id === workspaceId);
    if (!workspace) return;

    setCurrentWorkspace(workspace);
    setIsOpen(false);

    const pathname = location.pathname;

    // ── Workspace detail page: navigate to the new workspace's detail page ──
    // Pattern: /workspaces/<uuid>  (with optional trailing segments)
    const wsDetailMatch = pathname.match(/^(\/workspaces\/)([^/]+)(\/.*)?$/);
    if (wsDetailMatch) {
      navigate(`/workspaces/${workspaceId}${wsDetailMatch[3] ?? ''}`, {
        replace: true,
      });
      return;
    }

    // ── Tool pages: update ?workspace= in the URL in-place ──────────────────
    // Pattern: /tools/<slug>
    if (pathname.startsWith('/tools/')) {
      const next = new URLSearchParams(searchParams);
      next.set('workspace', workspaceId);
      navigate(`${pathname}?${next.toString()}`, { replace: true });
      return;
    }

    // ── All other pages: navigate to the workspace detail page ────────────
    // Defer to next tick so setCurrentWorkspace state update completes first
    setTimeout(() => {
      navigate(`/workspaces/${workspaceId}`, { replace: true });
    }, 0);
  };

  if (!currentWorkspace && visibleWorkspaces.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Briefcase className="w-4 h-4" />
        <span>No workspaces</span>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Current Workspace Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
      >
        <Briefcase className="w-4 h-4 text-gray-600" />
        <span className="font-medium text-sm text-gray-900">
          {currentWorkspace?.name || 'Select Workspace'}
        </span>
        {isCurrentHidden && <EyeOff className="w-3 h-3 text-amber-500" />}
        <ChevronDown
          className={`w-4 h-4 text-gray-600 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-96 overflow-y-auto">
            {/* Workspace List */}
            <div className="py-2">
              {visibleWorkspaces.length > 0 ? (
                visibleWorkspaces.map((workspace) => (
                  <button
                    key={workspace.id}
                    onClick={() => handleSelectWorkspace(workspace.id)}
                    className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">
                        {workspace.name}
                      </div>
                    </div>
                    {currentWorkspace?.id === workspace.id && (
                      <Check className="w-4 h-4 text-blue-600 flex-shrink-0 ml-2" />
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">
                  No workspaces available
                </div>
              )}
            </div>

            {/* Create New Workspace */}
            <div className="border-t border-gray-200 py-2">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/workspaces?new=true');
                }}
                className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-50 transition-colors text-left"
              >
                <Plus className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-900">
                  Create Workspace
                </span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
